import { Navigate } from 'react-router-dom';
import { useAudioStore } from '@/stores/audioStore';
import { NowPlayingPage } from '@/components/audio/nowplaying/NowPlayingPage';

/**
 * Route guard for `/now-playing`. Now-Playing is a full-screen takeover
 * that assumes an active audio session in the store. If none exists
 * (fresh tab, direct deep-link before playback, or audio was closed),
 * bounce to Start rather than rendering a blank shell.
 *
 * Gate on `storyId` (single-story `kind`) — `title` is always set
 * alongside `storyId` by `audioStore.load()`, and `hasPlayedOnce` is
 * intentionally NOT required here (the store-wired page handles
 * pre-playback state cleanly via AudioBigControls' Play button, which
 * kicks off `audioStore.play()`). The series-combined track has no
 * storyId and is controlled only from the season view, so it bounces too.
 *
 * Catalog (Wissenswelten) is admitted as well: it has no storyId, so its
 * branch keys on a seated catalog track (`kind==='catalog' && url && title`,
 * mirroring the MiniPlayer gate) — `loadCatalog` only sets `url`/`title` when
 * a Folge is loaded for playback. The story condition is left verbatim.
 *
 * Series-combined (the season Gesamt-Hörbuch) is now admitted too — same shape
 * as catalog (no storyId; keyed on a seated track) — so tapping its MiniPlayer
 * opens the full player instead of bouncing to Start.
 */
export function NowPlayingRoute() {
  const kind = useAudioStore((s) => s.kind);
  const storyId = useAudioStore((s) => s.storyId);
  const url = useAudioStore((s) => s.url);
  const title = useAudioStore((s) => s.title);

  const isStory = kind === 'story' && storyId;
  const isCatalog = kind === 'catalog' && !!url && !!title;
  const isSeriesCombined = kind === 'series-combined' && !!url && !!title;
  if (!isStory && !isCatalog && !isSeriesCombined) {
    return <Navigate to="/" replace />;
  }

  return <NowPlayingPage />;
}
